import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BadgeCheck, ShieldX } from "lucide-react";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { SiteFooter } from "@/components/landing/Footer";
import { Markdown } from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";

const Shell = ({ children, testid }) => (
  <div className="flex min-h-screen flex-col bg-white">
    <SiteHeader />
    <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-14 sm:px-6" data-testid={testid}>{children}</main>
    <SiteFooter />
  </div>
);

export function VerifyPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(id || "");
  const [r, setR] = useState(null);
  useEffect(() => { document.title = "Verify a certificate | Crowlearn"; if (id) axios.get(`${API}/verify/${encodeURIComponent(id)}`).then((x) => setR(x.data)).catch(() => setR({ found: false })); }, [id]);
  return (
    <Shell testid="verify-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Verify a certificate</h1>
      <form onSubmit={(e) => { e.preventDefault(); nav(`/verify/${q.trim()}`); }} className="mt-6 flex max-w-md gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Certificate ID, e.g. CRW-1A2B3C4D" aria-label="Certificate ID" required data-testid="verify-input" />
        <Button type="submit" className="bg-[#222d99] hover:bg-[#1a237e]" data-testid="verify-btn">Check</Button>
      </form>
      {r && (r.found ? (
        <div className={`mt-8 rounded-xl border p-6 ${r.valid ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`} data-testid="verify-result">
          <p className="flex items-center gap-2 text-lg font-bold">{r.valid ? <BadgeCheck className="h-5 w-5 text-emerald-700" /> : <ShieldX className="h-5 w-5 text-rose-700" />} {r.valid ? "Valid certificate" : "This certificate has been revoked"}</p>
          <p className="mt-2 text-slate-800">{r.title}{r.skill && ` · ${r.skill}`}</p>
          <p className="text-slate-700">Awarded to {r.holder} on {r.issued_at} · {r.cert_id}</p>
        </div>
      ) : <p className="mt-8 rounded-xl border border-slate-200 p-6 text-slate-700" data-testid="verify-not-found">No certificate matches this ID.</p>)}
    </Shell>
  );
}

export function BlogList() {
  const [posts, setPosts] = useState(null);
  useEffect(() => { document.title = "Blog | Crowlearn"; axios.get(`${API}/site/blog`).then((r) => setPosts(r.data)).catch(() => setPosts([])); }, []);
  return (
    <Shell testid="blog-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Blog</h1>
      {posts && !posts.length && <p className="mt-6 text-slate-700" data-testid="blog-empty">No posts yet.</p>}
      <ul className="mt-8 space-y-6">{(posts || []).map((p) => <li key={p.slug}><Link to={`/blog/${p.slug}`} className="text-xl font-semibold text-[#222d99] hover:underline" data-testid={`blog-link-${p.slug}`}>{p.title}</Link><p className="mt-1 text-slate-700">{p.excerpt}</p></li>)}</ul>
    </Shell>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { axios.get(`${API}/site/blog/${slug}`).then((r) => { setP(r.data); document.title = `${r.data.title} | Crowlearn`; }).catch(() => setP({ missing: true })); }, [slug]);
  return (
    <Shell testid="blog-post">
      <Link to="/blog" className="text-sm text-slate-700 hover:text-[#222d99]">← All posts</Link>
      {p?.missing ? <p className="mt-6">Post not found.</p> : p && <><h1 className="mt-4 text-3xl font-extrabold text-slate-900 sm:text-4xl">{p.title}</h1><div className="mt-6"><Markdown text={p.body || ""} /></div></>}
    </Shell>
  );
}

export function SiteBanner() {
  const [b, setB] = useState([]);
  useEffect(() => { axios.get(`${API}/site/banners`).then((r) => setB(r.data)).catch(() => {}); }, []);
  if (!b.length) return null;
  return (
    <div className="bg-[#222d99] px-4 py-2 text-center text-sm text-white" role="region" aria-label="Announcement" data-testid="site-banner">
      {b[0].text} {b[0].link_url?.startsWith("/") && <Link to={b[0].link_url} className="ml-1 font-semibold underline">{b[0].link_label || "Learn more"}</Link>}
    </div>
  );
}

export function useSiteContent(page) {
  const [c, setC] = useState(null);
  useEffect(() => { axios.get(`${API}/site/page/${page}`).then((r) => setC(r.data)).catch(() => setC({})); }, [page]);
  useEffect(() => {
    if (!c?.seo_description) return;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = c.seo_description;
  }, [c]);
  return c;
}

export function useSiteFaqs(fallback) {
  const [f, setF] = useState(fallback);
  useEffect(() => { axios.get(`${API}/site/faqs`).then((r) => { if (r.data.length) setF(r.data.map((x) => ({ q: x.question, a: x.answer }))); }).catch(() => {}); }, []);
  return f;
}
